import { cloneDeep } from "lodash";
import { isWebsiteBuilderTabset, IWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderTab } from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import {
  IWebsiteBuilderEditorHistoryActionBase,
  IWebsiteBuilderHistorySnapshot,
  WebsiteBuilderEditorActionType,
} from "../../websiteBuilderEditorAction";
import { IDeleteTabAction } from "./deleteTab";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";

export interface IAddTabActionPayload {
  tabsetId: string;
  tab: IWebsiteBuilderTab;
  index: number;
  prevDefaultTabId: string;
  nextDefaultTabId: string;
}

export interface IAddTabAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.ADD_TAB;
  payload: IAddTabActionPayload;
}

export function getAddTabInverseAction(action: IAddTabAction): IDeleteTabAction {
  const {
    tabsetId,
    tab,
    index,
    prevDefaultTabId,
    nextDefaultTabId,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.DELETE_TAB,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      tabsetId,
      tab: cloneDeep(tab),
      index,
      prevDefaultTabId: nextDefaultTabId,
      nextDefaultTabId: prevDefaultTabId,
    },
  };
}

type TAddTabTraversalNode = IWebsiteBuilderTabset | IWebsiteBuilderGeneralTraversalNode;

function addTabTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  tabsetId: string,
  tab: IWebsiteBuilderTab,
  index: number,
  defaultTabId: string,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TAddTabTraversalNode): TAddTabTraversalNode {
    if (isWebsiteBuilderTabset(node)) {
      if (node.id === tabsetId) {
        return {
          ...node,
          container: {
            ...node.container,
            settings: {
              ...node.container.settings,
              defaultTabId,
            },
          },
          children: [
            ...node.children.slice(0, index),
            tab,
            ...node.children.slice(index),
          ],
        };
      }

      return node;
    } else if (node.children) {
      return {
        ...node,
        children: node.children.map(traverse),
      };
    } else {
      return node;
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applyAddTabChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IAddTabAction,
): IWebsiteBuilderHistorySnapshot {
  const { tabsetId, tab, index, nextDefaultTabId } = action.payload;

  return addTabTraversal(header, root, footer, tabsetId, cloneDeep(tab), index, nextDefaultTabId);
}
